import { useParams } from 'react-router-dom';
import { useConfig } from '../context/ConfigContext';
import CheckoutPageShell from './CheckoutPageShell';
import OpenPlayCheckoutSummary from './OpenPlayCheckoutSummary';
import { useCheckoutPageSession } from '../hooks/useCheckoutPageSession';
import { discardUnpaidOpenPlayJoin } from '../utils/openPlayUtil';
import { hasReceipt } from '../utils/receiptUtil';
import { CHECKOUT_EVENTS } from '../constants/bookingConst';

/**
 * The Open Play join checkout in its own browser tab
 * (/open-play-checkout/:sessionId) — the court-booking CheckoutPage's
 * counterpart.
 *
 * Opened by OpenPlayModal (see hooks/useCheckoutTab.js), which stays open
 * behind it showing a "complete your payment" card. Outcomes reported back:
 *   RECEIPT_UPLOADED  receipt stored, join submitted
 *   SUBMITTED         guest tapped Done — the modal shows its confirmation
 *   CANCELLED         guest went Back / Start Over — the unpaid join was
 *                     discarded and the modal returns to the player form
 *
 * There is no automatic release on timeout: an unpaid join holds no spot, so
 * the expired screen just offers Start Over, as the modal did.
 *
 * @returns {JSX.Element}
 */
function OpenPlayCheckoutPage() {
  const { sessionId } = useParams();
  const { config } = useConfig();
  const { session, phase, updateRecord, post, endCheckout, runOnce } = useCheckoutPageSession(sessionId);

  const join = session?.bookingData || null;
  const context = session?.context || null;

  /**
   * Receipt stored (uploadOpenPlayScreenshot already flipped the join to
   * 'pending' and wrote the audit entry) — remember it and tell the modal.
   * @param {Object} updatedJoin
   */
  const handleReceiptUploaded = (updatedJoin) => {
    updateRecord(updatedJoin);
    post(CHECKOUT_EVENTS.RECEIPT_UPLOADED, { bookingData: updatedJoin });
  };

  /**
   * "Done" — hand the guest back to the modal for the confirmation.
   */
  const handleDone = () => {
    endCheckout(CHECKOUT_EVENTS.SUBMITTED, { bookingData: join });
  };

  /**
   * Back / Start Over. Throws the unpaid join away so pressing Continue again
   * doesn't write a second record — discardUnpaidOpenPlayJoin re-checks the
   * stored status, so a join that was paid meanwhile is never deleted; that
   * case finishes like Done instead.
   */
  const handleBack = () => runOnce(async () => {
    if (hasReceipt(join)) {
      handleDone();
      return;
    }
    try {
      if (join?.id) await discardUnpaidOpenPlayJoin(join.id);
    } catch (err) {
      // A leftover unpaid record is harmless — it counts for nothing and is
      // invisible to the admin — so a failure must not trap the guest here.
      console.error('Could not discard the unpaid Open Play join:', err);
    }
    endCheckout(CHECKOUT_EVENTS.CANCELLED);
  });

  return (
    <CheckoutPageShell
      phase={phase}
      inactiveMessage="We couldn’t find an Open Play join in progress for this page. Please go back to the booking page and start again."
    >
      {join && (
        <OpenPlayCheckoutSummary
          join={join}
          context={context}
          config={config}
          onReceiptUploaded={handleReceiptUploaded}
          onDone={handleDone}
          onBack={handleBack}
        />
      )}
    </CheckoutPageShell>
  );
}

export default OpenPlayCheckoutPage;
